import React, { useEffect, useState } from 'react';
import ModalBasic from '@/components/ModalBasic';
import { validateStepDraft } from './constants';

const emptyDraft = { name: '', sql: '', notes: '', enabled: true };

/**
 * Create or edit one transform step.
 *
 * Step order is deliberately not editable here — the API ignores it on update,
 * because reordering has to rewrite the whole chain. Use drag-to-reorder in the
 * list instead.
 */
const StepEditorModal = ({ open, step, steps, sources = [], storeId, onClose, onSubmit, saving }) => {
  const [draft, setDraft] = useState(emptyDraft);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setDraft(
      step
        ? {
            name: step.name || '',
            sql: step.sql || '',
            notes: step.notes || '',
            enabled: step.enabled !== false,
          }
        : emptyDraft
    );
    setError(null);
  }, [open, step]);

  const submit = async () => {
    const message = validateStepDraft(draft, steps, step?.id ?? null, sources);
    if (message) {
      setError(message);
      return;
    }
    setError(null);
    await onSubmit(draft);
  };

  return (
    <ModalBasic
      id="transform-step-editor"
      title={step ? `Edit step "${step.name}"` : 'Add transform step'}
      modalOpen={open}
      setModalOpen={onClose}
      width="3xl"
    >
      <div className="space-y-4">
        <p className="text-sm text-slate-600 dark:text-slate-300">
          The parsed CSV is registered as a table named <code className="font-mono">source</code>.
          Steps run in order and each result is registered under its own name, so a later step can
          select from an earlier one. The <strong>last enabled step</strong> must emit the canonical{' '}
          <code className="font-mono">retailer_*</code> columns.
        </p>

        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Name
          </label>
          <input
            type="text"
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            placeholder="e.g. price_round"
            className="w-full px-3 py-2 font-mono border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
          />
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            A plain identifier — the pipeline interpolates it into a CREATE TABLE statement.
            Renaming a step that later steps select from is refused.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            SQL
          </label>
          <textarea
            value={draft.sql}
            onChange={(e) => setDraft({ ...draft, sql: e.target.value })}
            rows={12}
            spellCheck={false}
            placeholder={'SELECT\n  ItemNum AS retailer_sku,\n  round(Price, 2) AS retailer_price\nFROM source'}
            className="w-full px-3 py-2 font-mono text-xs border border-slate-300 dark:border-slate-600 rounded-md bg-slate-50 dark:bg-slate-900/40 text-slate-900 dark:text-slate-100"
          />
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            A single <code className="font-mono">SELECT</code> or <code className="font-mono">WITH</code>{' '}
            statement.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Notes
          </label>
          <textarea
            value={draft.notes}
            onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
            rows={2}
            placeholder="Fidelity or dialect caveats worth flagging before cutover"
            className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
          />
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            On imported steps this is what the Glue importer recorded: expressions it carried over,
            and constructs it could not translate.
          </p>
        </div>

        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={draft.enabled}
            onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })}
            className="mt-0.5 form-checkbox"
          />
          <span className="text-sm text-slate-700 dark:text-slate-300">
            Enabled
            <span className="block text-xs text-slate-500 dark:text-slate-400">
              Disabling any single step makes the pipeline skip this store&rsquo;s entire chain and
              fall back to the column-mapping path.
            </span>
          </span>
        </label>

        {!step && storeId && (
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Appended to the end of <span className="font-mono">{storeId}</span>&rsquo;s chain. Drag
            to reorder afterwards.
          </p>
        )}

        {error && (
          <div className="rounded border border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-900/20 p-3">
            <p className="text-sm text-rose-700 dark:text-rose-300">{error}</p>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 border border-slate-300 dark:border-slate-600 rounded-md hover:bg-slate-50 dark:hover:bg-slate-700"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : step ? 'Update step' : 'Add step'}
          </button>
        </div>
      </div>
    </ModalBasic>
  );
};

export default StepEditorModal;
